import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Plus, ScrollText, X } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const STATUS = {
  draft: "bg-slate-100 text-slate-500 dark:bg-ink-panel dark:text-slate-400",
  issued: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
};
const INPUT = "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-ink-line dark:bg-ink-panel dark:text-ink-text";
const money = (c, v) => `${c || ""} ${Number(v || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
const TERMS = ["50:50", "80:20", "100_prepaid"];

export default function ProformaInvoices() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try { setRows((await api.get("/finance/proforma_invoice")).data); }
    catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  return (
    <div className="space-y-6" data-testid="proforma-invoices-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-slate-400">Financial Anchor</p>
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 dark:text-ink-text">Pro Forma Invoices</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">The PI is the financial anchor. A Booking is born only after a human verifies the required payment.</p>
        </div>
        {hasPermission("proformas.create") && (
          <button onClick={() => setOpen(true)} data-testid="pi-new-button"
            className="inline-flex items-center gap-2 rounded-lg bg-ink-gold px-4 py-2.5 text-sm font-semibold text-[#0B1120] hover:brightness-110">
            <Plus className="h-4 w-4" /> New Pro Forma Invoice
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-sky-600 dark:text-ink-gold" /></div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-12 text-center dark:border-ink-line" data-testid="pi-empty-state">
          <ScrollText className="mx-auto h-8 w-8 text-slate-400" />
          <p className="mt-3 text-sm font-medium text-slate-600 dark:text-slate-300">No Pro Forma Invoices yet</p>
          <p className="mt-1 text-xs text-slate-400">Create one from an accepted quotation to begin the financial flow.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-ink-line dark:bg-ink-card">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="pi-table">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500 dark:border-ink-line dark:bg-ink/40">
                  <th className="px-5 py-3 font-medium">PI #</th>
                  <th className="px-5 py-3 font-medium">Customer</th>
                  <th className="px-5 py-3 font-medium">Quotation</th>
                  <th className="px-5 py-3 font-medium">Amount</th>
                  <th className="px-5 py-3 font-medium">Terms</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium">Payment</th>
                  <th className="px-5 py-3 font-medium">Booking #</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} onClick={() => navigate(`/finance/proforma-invoices/${r.id}`)}
                    className="cursor-pointer border-b border-slate-100 last:border-0 hover:bg-slate-50 dark:border-ink-line dark:hover:bg-ink-panel/40"
                    data-testid={`pi-row-${r.id}`}>
                    <td className="px-5 py-3 font-mono-tf text-xs font-semibold text-slate-700 dark:text-ink-gold">{r.document_number || "— draft —"}</td>
                    <td className="px-5 py-3 font-medium text-slate-800 dark:text-ink-text">{r.customer_name || "—"}</td>
                    <td className="px-5 py-3 font-mono-tf text-xs text-slate-600 dark:text-slate-300">{r.quotation_number || "—"}</td>
                    <td className="px-5 py-3 font-mono-tf text-slate-700 dark:text-slate-200">{money(r.currency, r.total_amount)}</td>
                    <td className="px-5 py-3 text-slate-600 dark:text-slate-300">{r.payment_terms}</td>
                    <td className="px-5 py-3"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS[r.status]}`}>{r.status}</span></td>
                    <td className="px-5 py-3">
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${r.payment_verified ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300" : "bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300"}`} data-testid={`pi-payment-${r.id}`}>
                        {r.payment_verified ? "Verified" : "Pending"}
                      </span>
                    </td>
                    <td className="px-5 py-3 font-mono-tf text-xs text-slate-600 dark:text-slate-300">{r.booking_number || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {open && <CreatePIModal onClose={() => setOpen(false)} onCreated={(id) => { setOpen(false); navigate(`/finance/proforma-invoices/${id}`); }} />}
    </div>
  );
}

function CreatePIModal({ onClose, onCreated }) {
  const [quotes, setQuotes] = useState([]);
  const [qid, setQid] = useState("");
  const [terms, setTerms] = useState("50:50");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try { setQuotes((await api.get("/quotations", { params: { status: "accepted" } })).data); }
      catch (e) { toast.error(apiError(e)); }
    })();
  }, []);
  const sel = quotes.find((q) => q.id === qid);

  const submit = async () => {
    if (!sel) { toast.error("Select an accepted quotation"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/finance/proforma_invoice", {
        customer_id: sel.customer_id, quotation_id: sel.id,
        currency: sel.currency, total_amount: String(sel.customer_total ?? 0), payment_terms: terms,
      });
      toast.success("Pro Forma Invoice draft created");
      onCreated(data.id);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="pi-create-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl dark:border-ink-line dark:bg-ink-card">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-lg font-bold text-slate-900 dark:text-ink-text">New Pro Forma Invoice</h2>
          <button onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-ink-panel"><X className="h-5 w-5" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">Accepted Quotation</label>
            <select value={qid} onChange={(e) => setQid(e.target.value)} data-testid="pi-quotation-select" className={INPUT}>
              <option value="">Select a quotation…</option>
              {quotes.map((q) => <option key={q.id} value={q.id}>{(q.revision_label || q.quotation_number)} — {q.customer_name} — {money(q.currency, q.customer_total)}</option>)}
            </select>
          </div>
          {sel && (
            <div className="rounded-lg bg-slate-50 p-3 text-sm dark:bg-ink-panel/40">
              <div className="flex justify-between"><span className="text-slate-500">Customer</span><span className="font-medium text-slate-800 dark:text-ink-text">{sel.customer_name}</span></div>
              <div className="mt-1 flex justify-between"><span className="text-slate-500">Total amount</span><span className="font-mono-tf text-slate-800 dark:text-ink-text">{money(sel.currency, sel.customer_total)}</span></div>
            </div>
          )}
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">Payment Terms</label>
            <select value={terms} onChange={(e) => setTerms(e.target.value)} data-testid="pi-terms-select" className={INPUT}>
              {TERMS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 dark:border-ink-line dark:text-slate-300">Cancel</button>
          <button onClick={submit} disabled={saving || !sel} data-testid="pi-create-submit"
            className="inline-flex items-center gap-2 rounded-lg bg-ink-gold px-4 py-2 text-sm font-semibold text-[#0B1120] disabled:opacity-50">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Create Draft
          </button>
        </div>
      </div>
    </div>
  );
}
